import { Color, Group, Matrix, Path, Point, Project, } from "paper/dist/paper-core";
import { PaperUtils } from "../utilities/paper-utils";
import { defaultCircleEntitySetttings, defaultEntityPathSettings, defaultGridLineSettings, defaultGridTextSettings, defaultReferenceLineSettings, defaultTextSettings, KEY_POINT_CIRCLE_COLOR, pixelAttenuationOffset, PROFILE_DISTANCE_DECIMAL_PLACES, PROFILE_HEIGHT_DECIMAL_PLACES, } from "../settings/altitude-view-settings";
export class LineProfileView {
    _project;
    _view;
    _canvas;
    _zoomFactor = 1.05;
    // to keep all grid lines together and easy to delete all grid lines at same time.
    _gridGroup;
    _gridSizeInPixel = 50;
    _outlineRenderGroup;
    _gridText;
    isDragging = false;
    startPoint;
    _currentEntityID;
    _presentation;
    _referenceLine;
    _entityRenderGroup;
    _textRenderGroup;
    _lengthTextRenderGroup;
    _heightTextRenderGroup;
    _controlPointRenderGroup;
    _lastProfileArgs;
    get currentEntityID() {
        return this._currentEntityID;
    }
    constructor(presentation) {
        this._presentation = presentation;
        this._canvas = document.createElement("canvas");
        // Setup canvas here
        this._canvas.style.width = "100%";
        this._canvas.style.height = "100%";
        this._canvas.style.backgroundColor = "#566573";
        this._project = new Project(this._canvas);
        this._project.activate();
        this._view = this._project.view;
        this._view.transform(new Matrix(1, 0, 0, -1, 0, 0));
        //to keep all grid lines in one group
        this._gridGroup = new Group();
        this._gridText = new Group();
        this._entityRenderGroup = new Group();
        this._textRenderGroup = new Group();
        this._lengthTextRenderGroup = new Group();
        this._textRenderGroup.addChild(this._lengthTextRenderGroup);
        this._heightTextRenderGroup = new Group();
        this._textRenderGroup.addChild(this._heightTextRenderGroup);
        this._controlPointRenderGroup = new Group();
        //Create grid lines
        this.changeGridSize(this._gridSizeInPixel);
        this._canvas.addEventListener("wheel", (event) => {
            PaperUtils.zoom(event, this._zoomFactor, this._view);
            this.changeGridSize(this._gridSizeInPixel);
            this._attenuate();
        });
        this._canvas.addEventListener("mousedown", (event) => {
            this.isDragging = true;
            this.startPoint = PaperUtils.getCoordinateOnView(this._view, event.pageX, event.pageY);
        });
        this._canvas.addEventListener("mousemove", (event) => {
            if (this.isDragging) {
                const point = PaperUtils.getCoordinateOnView(this._view, event.pageX, event.pageY);
                this._view.translate(point.subtract(this.startPoint));
                this.changeGridSize(this._gridSizeInPixel);
                this._view.update();
            }
        });
        this._canvas.addEventListener("mouseup", () => {
            this.isDragging = false;
        });
    }
    _activateView() {
        this._project.activate();
    }
    _attenuate() {
        //attenuate Entities
        this._outlineRenderGroup &&
            this._outlineRenderGroup.children.forEach((l) => PaperUtils.attenuateLines(l, this._view, defaultEntityPathSettings.defaultPixelSize));
        //attenuate Grids
        this._gridGroup &&
            this._gridGroup.children.forEach((line) => PaperUtils.attenuateLines(line, this._view, defaultGridLineSettings.defaultPixelSize));
        this._gridText &&
            this._gridText.children.forEach((text) => {
                PaperUtils.attenuateEntites(text, this._view, defaultGridTextSettings.defaultPixelSize);
            });
        this._entityRenderGroup &&
            this._entityRenderGroup?.children.forEach((l) => PaperUtils.attenuateLines(l, this._view, defaultEntityPathSettings.defaultPixelSize));
        this._lengthTextRenderGroup.children.forEach((text) => {
            PaperUtils.attenuateEntites(text, this._view, defaultTextSettings.defaultPixelSize);
            PaperUtils.setAttenuatedPos(text, pixelAttenuationOffset.lentghTextOffsetAltitudeView, this._view);
        });
        this._heightTextRenderGroup.children.forEach((text) => {
            PaperUtils.attenuateEntites(text, this._view, defaultTextSettings.defaultPixelSize);
            PaperUtils.setAttenuatedPos(text, pixelAttenuationOffset.heightTextOffsetAltitudeView, this._view);
        });
        //attenuate Entities
        this._controlPointRenderGroup &&
            this._controlPointRenderGroup.children.forEach((circle) => {
                PaperUtils.attenuateEntites(circle, this._view, defaultCircleEntitySetttings.defaultPixelSize);
            });
        this._view.requestUpdate();
    }
    clearView() {
        if (this._referenceLine) {
            this._referenceLine.remove();
            this._referenceLine = undefined;
        }
        if (this._outlineRenderGroup) {
            this._outlineRenderGroup.removeChildren();
            this._outlineRenderGroup = undefined;
        }
        //remove grid text
        this._gridText.removeChildren();
        this._lengthTextRenderGroup.removeChildren();
        this._heightTextRenderGroup.removeChildren();
        // remove control point entities
        this._controlPointRenderGroup.removeChildren();
    }
    //To do : If needed can be rendered at start of arbitrary line.
    _createReferenceLine() {
        this._referenceLine = PaperUtils.createLine(new Point(0, 10000), new Point(0, -10000), defaultReferenceLineSettings.strokeColor);
        this._referenceLine.scale(0.1);
        this._referenceLine.data.name = "secViewPlane";
        this._entityRenderGroup.addChild(this._referenceLine);
    }
    resizeView(size) {
        if (size.height && size.width) {
            this._project.view.viewSize.height = size.height;
            this._project.view.viewSize.width = size.width;
            this._view.update();
            this.changeGridSize(this._gridSizeInPixel);
        }
    }
    _createPath(pointArr, color) {
        const path = new Path();
        path.strokeColor = new Color(color);
        pointArr.forEach((arr) => {
            path.add(new Point(arr[0], arr[1]));
        });
        return path;
    }
    renderTotalDistance(pathPoints, presentation) {
        if (pathPoints.length === 0)
            return;
        let textString;
        let dist;
        pathPoints.forEach((p) => {
            const nextPoint = p;
            if (nextPoint !== undefined) {
                dist = nextPoint.x;
                textString = ` [ ${presentation.formatFixed(dist, PROFILE_DISTANCE_DECIMAL_PLACES)}${presentation.getUnitLabel()} ]`;
            }
        });
        const pos = pathPoints[pathPoints.length - 1];
        const lengthText = PaperUtils.createText(pos, defaultTextSettings.fillColor, textString, 1);
        lengthText.data.moveDir = new Point(0, 1);
        lengthText.data.refPosition = lengthText.position.clone();
        this._lengthTextRenderGroup.addChild(lengthText);
    }
    renderHeightText(pathPoints, presentation, keyPointIndices, slopeBreakIndices) {
        pathPoints.forEach((point, index) => {
            const isKeyPoint = keyPointIndices?.includes(index) ?? true;
            const isSlopeBreak = slopeBreakIndices?.includes(index) ?? false;
            // Render height text and circle for key points (BP/EP/BC/EC/IP) or slope breaks
            if (isKeyPoint || isSlopeBreak) {
                const heightTextString = presentation.formatFixed(point.y, PROFILE_HEIGHT_DECIMAL_PLACES) +
                    presentation.getUnitLabel();
                const heightText = PaperUtils.createText(point, defaultTextSettings.fillColor, heightTextString, 1);
                heightText.data.moveDir = new Point(0, 1);
                heightText.data.refPosition = heightText.position.clone();
                this._heightTextRenderGroup.addChild(heightText);
                const circle = PaperUtils.createPoint(point.clone(), defaultCircleEntitySetttings.radius, new Color(KEY_POINT_CIRCLE_COLOR));
                circle.data.name = index.toString();
                circle.position.set(point.clone());
                this._controlPointRenderGroup.addChild(circle);
            }
        });
    }
    renderData(lineID, data, fit) {
        this._activateView();
        this.clearView();
        if (lineID === undefined || data === undefined) {
            return;
        }
        this._lastProfileArgs = { lineID, data };
        this._currentEntityID = lineID;
        const outlineData = data;
        const rect = this._canvas.getBoundingClientRect();
        this.resizeView({ width: rect.width, height: rect.height });
        const presentation = this._presentation;
        const lineCloudOutlineColor = "#FFFF00";
        const pointCloudOutlineColor = "#00FF00";
        this._outlineRenderGroup = new Group();
        //Render Arbitrary Outline
        const lineWorksPath = this._createPath(outlineData.lineWorksOutline, lineCloudOutlineColor);
        this._outlineRenderGroup.addChild(lineWorksPath);
        //Render Pointcloud Outline
        outlineData.pointCloudOutline.forEach((outline2d) => {
            this._outlineRenderGroup.addChild(this._createPath(outline2d, pointCloudOutlineColor));
        });
        const pathPoints = lineWorksPath.segments.map((s) => s.point.clone());
        //Render Total Distance of Arbitrary Line.
        this.renderTotalDistance(pathPoints, presentation);
        //Render height for each control point of arbitrary line.
        this.renderHeightText(pathPoints, presentation, outlineData.keyPointIndices, outlineData.slopeBreakIndices);
        if (fit) {
            //fit the entities in current view
            const bounds = this._outlineRenderGroup.bounds;
            PaperUtils.fitView(this._view, bounds.center, bounds.width, bounds.height);
        }
        this.changeGridSize(this._gridSizeInPixel);
        this._attenuate();
    }
    setBackgroundColor(color) {
        const tempColor = color ? color : "#566573";
        this._canvas.style.backgroundColor = tempColor;
    }
    updatePresentation(presentation) {
        this._presentation = presentation;
        if (this._lastProfileArgs) {
            this.renderData(this._lastProfileArgs.lineID, this._lastProfileArgs.data, false);
        }
        else {
            this._activateView();
            this.changeGridSize(this._gridSizeInPixel);
        }
    }
    changeGridSize(step) {
        this._activateView();
        PaperUtils.createGrid(this._view, step, this._gridGroup, this._gridText, this._presentation);
        this._attenuate();
    }
    getCanvas() {
        return this._canvas;
    }
}
//# sourceMappingURL=view-2d-line-profile.js.map